import type { MouseEvent } from 'react';

import { goBack, goUp } from '../app/router.tsx';
import { Wedge } from './icons.tsx';

/** `/` (Today), or Play: `/play`, or Play with its stack or search (`/play?stack=missed`). */
export type BackTo = string;

/**
 * "‹ Today" (or "‹ Play" from a practice puzzle or a game opened from Play): the way home on
 * every screen without the tab bar, top left, where people reach for back. The wedge turned
 * round. It goes back through history when Today (or Play) is below, else replaces: never a
 * push (app/router.tsx).
 *
 * `mode="history"` is for a leaf page reached from anywhere (Privacy): "‹ Back" goes back to
 * the screen you came from, as the system's back does, or to Today on a cold open (UX-021).
 *
 * The shared `.btn.back` (buttons.css): a 36px pill in the 30px masthead row, a 44px target.
 */
export function BackPill({ to = '/', mode = 'up', className }: { to?: BackTo; mode?: 'up' | 'history'; className?: string }) {
	const history = mode === 'history';
	const label = history ? 'Back' : to.startsWith('/play') ? 'Play' : 'Today';
	const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
		if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
		e.preventDefault();
		if (history) goBack();
		else goUp(to);
	};
	return (
		<a
			className={className ? `btn back ${className}` : 'btn back'}
			href={history ? '/' : to}
			onClick={onClick}
			aria-label={history ? 'Back' : `Back to ${label}`}
		>
			<Wedge direction="left" className="back-wedge" />
			<span aria-hidden="true">{label}</span>
		</a>
	);
}
